import { emptyStyle } from "../constants";
import { markdownSerializer } from "../helpers/markdown";
import {
	blockStyleFromMinimize,
	minimizeBlockStyle,
	minimizeTextNodeStyle,
	textNodeStyleFromMinimize,
} from "../helpers/minimizer";
import { copyWithout, getTextHash } from "../helpers/other";
import { parser } from "../helpers/parser";
import { serializer } from "../helpers/serializer";
import { Manager } from "../Manager";
import { Action, IRichTextEventSystem, IStyle, Modifier, RichTextEvents } from "../types";
import { SelectionHelper } from "./../SelectionHelper";
import { Block, SplitResult } from "./Block";
import { RemoveTextResult, TextNode } from "./TextNode";
import { isEmptyBlockStyle } from "../helpers/styles";

interface ITempTextNode {
	offset: number;
	textIndex: number;
	blockIndex: number;
	style: IStyle;
}

interface IEditorConfig {
	events: IRichTextEventSystem;
	uid: string;
	getRootView: () => any;
	// after balance need to call repaint
	repaint: () => void;
}

export class Editor {
	public selection: SelectionHelper;
	public manager: Manager;

	public blocks: Block[];

	private _events: IRichTextEventSystem;
	private _getRootView: () => any;
	private _repaint: () => void;

	private _tempNode: ITempTextNode;

	constructor({ events, uid, getRootView, repaint }: IEditorConfig) {
		this._events = events;
		this._repaint = repaint;
		this._getRootView = getRootView;

		this.selection = new SelectionHelper(this, uid, events);
		this.clear();

		this.manager = new Manager(this);

		this._events.on(RichTextEvents.change, action => {
			if (action !== Action.update) {
				this._tempNode = null;
			}
		});
		this._events.on(RichTextEvents.selectionChange, () => {
			this._tempNode = null;
		});
	}
	clear() {
		this.blocks = [new Block([new TextNode("", emptyStyle)])];
		this.selection.clear();
	}
	getState() {
		return JSON.stringify({
			b: this.blocks.map(block => ({
				s: minimizeBlockStyle(block.style),
				n: block.textNodes.map(textNode => ({
					s: minimizeTextNodeStyle(textNode.style),
					t: textNode.text,
				})),
			})),
			s: this.selection.getCompact(),
		});
	}
	setState(state: string) {
		const raw = JSON.parse(state);
		this.blocks = raw.b.map(({ s, n }) => {
			const textNodes = n.map((node: any) => new TextNode(node.t, textNodeStyleFromMinimize(node.s)));
			const block = new Block(textNodes, blockStyleFromMinimize(s));
			return block;
		});
		this.selection.setCompact(raw.s);
	}
	parse(value: any, mode: "html" | "markdown" | "inner" = "html") {
		if (mode === "html" || mode === "markdown") {
			const blocks = parser(value, mode);
			this.blocks = blocks;
		} else {
			this.blocks = value.map(
				(block: any) =>
					new Block(
						block.textNodes.map((textNode: any) => new TextNode(textNode.text, textNode.style)),
						block.style
					)
			);
		}
		this.selection.set({
			range: false,
			left: {
				blockIndex: this.blocks.length - 1,
				...this.blocks[this.blocks.length - 1].getBlockEnd(),
			},
		});
		return true;
	}
	serialize(mode: "html" | "markdown" | "text" = "html") {
		this.balance();
		if (mode === "markdown") {
			return markdownSerializer(this.blocks);
		}
		if (mode === "html") {
			return serializer(this.blocks);
		}
		if (mode === "text") {
			return this.blocks.reduce(
				(blockText, block, i) =>
					blockText +
					(i > 0 ? "\n" : "") +
					block.textNodes.reduce((text, textNode) => text + textNode.text, ""),
				""
			);
		}
	}
	getTextElement = (blockIndex, textIndex) => {
		const id = getTextHash(blockIndex, textIndex);
		const rootView = this._getRootView();
		return (
			rootView.refs && rootView.refs[id] && rootView.refs[id].el && rootView.refs[id].el.childNodes[0]
		);
	};
	removeBlock(index: number) {
		this.blocks.splice(index, 1);
		if (this.blocks.length === 0) {
			this.clear();
		}
	}
	insertBlock() {
		this._splitCarret();
		const { range, left } = this.selection.get();
		if (range) {
			return;
		}

		const currentLineNodes = this.blocks[left.blockIndex].textNodes;
		let nextLineNodes = currentLineNodes.splice(left.textIndex + (left.offset ? 1 : 0), Infinity); // Infinity to remove all after left
		let nextLineBlockStyle = this.blocks[left.blockIndex].style;

		if (nextLineNodes.length === 0) {
			const style = this.blocks[left.blockIndex].textNodes[left.textIndex].style;
			nextLineNodes = [new TextNode("", copyWithout(style, { link: true }))];
			nextLineBlockStyle = copyWithout(nextLineBlockStyle, {
				blockquote: true,
				style: true,
			});
			nextLineBlockStyle[Modifier.style] = "p";
		} else {
			const link =
				currentLineNodes[currentLineNodes.length - 1] &&
				currentLineNodes[currentLineNodes.length - 1].style.link;
			if (link) {
				for (const textNode of nextLineNodes) {
					if (textNode.style.link === link) {
						textNode.style = copyWithout(textNode.style, { link: true });
					} else {
						break;
					}
				}
			}
		}

		if (currentLineNodes.length === 0) {
			this.blocks[left.blockIndex].clear();
		}

		const block = new Block(nextLineNodes, nextLineBlockStyle);
		this.blocks.splice(left.blockIndex + 1, 0, block);
		this.selection.set({
			range,
			left: {
				offset: 0,
				textIndex: 0,
				blockIndex: left.blockIndex + 1,
			},
		});
	}
	eachSelectedNode(fn: (textNode: TextNode, block?: Block) => any) {
		const { range, left, right } = this.selection.get();

		if (range) {
			for (let i = left.blockIndex; i <= right.blockIndex; i++) {
				let startNode = 0;
				let endNode = this.blocks[i].textNodes.length - 1;

				if (i === left.blockIndex) {
					startNode = left.textIndex;
				}
				if (i === right.blockIndex) {
					endNode = right.textIndex;
				}

				for (let j = startNode; j <= endNode; j++) {
					fn(this.blocks[i].textNodes[j], this.blocks[i]);
				}
			}
		} else {
			if (left) {
				fn(this.blocks[left.blockIndex].textNodes[left.textIndex], this.blocks[left.blockIndex]);
			}
		}
	}
	eachSelectedBlock(fn: (block: Block, index?: number) => any) {
		const { range, left, right } = this.selection.get();

		if (range) {
			for (let i = left.blockIndex; i <= right.blockIndex; i++) {
				fn(this.blocks[i], i);
			}
		} else {
			fn(this.blocks[left.blockIndex]);
		}
	}
	copy() {
		const { range } = this.selection.get();

		if (!range) {
			return false;
		}

		this._splitRange();

		const editorContent = [];
		let raw = "";

		const { left, right } = this.selection.get();
		this.eachSelectedBlock((block, index) => {
			let startIndex = 0;
			let endIndex = block.textNodes.length - 1;

			const textNodes = [];
			editorContent.push({
				style: block.style,
				textNodes,
			});
			if (index === left.blockIndex) {
				startIndex = left.textIndex;
			}

			if (index === right.blockIndex) {
				endIndex = right.textIndex;
			}

			for (let i = startIndex; i <= endIndex; i++) {
				const textNode = block.textNodes[i];
				textNodes.push({
					style: textNode.style,
					text: textNode.text,
				});
				raw += textNode.text;
			}
			if (index < right.blockIndex) {
				raw += "\n";
			}
		});
		return {
			content: editorContent,
			raw,
		};
	}
	cut() {
		const result = this.copy();
		this._splitRange();
		this._removeRange();

		return result;
	}
	paste(value, mode: "text" | "inner" | "html" = "text") {
		if (mode === "text") {
			return this.add(value);
		} else {
			const { range } = this.selection.get();

			if (range) {
				this._splitRange();
				this._removeRange();
			}

			const { left } = this.selection.get();

			const [firstBlock, ...blocks] = mode === "inner" ? JSON.parse(value) : parser(value, "html");

			let lastLineIndex;

			if (this.blocks[left.blockIndex].isEmpty()) {
				// destroy empty node
				this.blocks[left.blockIndex].textNodes = [];
				this.blocks[left.blockIndex].style = firstBlock.style;
			} else {
				// split node before paste
				const splitResult = this.blocks[left.blockIndex].splitTextNode(left.textIndex, left.offset);
				switch (splitResult) {
					case SplitResult.offsetStart:
						lastLineIndex = left.textIndex;
						break;
					case SplitResult.offsetEnd:
					case SplitResult.complete:
						lastLineIndex = left.textIndex + 1;
						break;
				}
			}
			let currentLineNodes;
			let lastLineNodes;

			if (lastLineIndex > 0) {
				currentLineNodes = this.blocks[left.blockIndex].textNodes.slice(0, lastLineIndex);
				lastLineNodes = this.blocks[left.blockIndex].textNodes.slice(lastLineIndex, Infinity);
			} else {
				currentLineNodes = [];
				lastLineNodes = this.blocks[left.blockIndex].textNodes;
			}

			for (const textNode of firstBlock.textNodes) {
				currentLineNodes.push(new TextNode(textNode.text, textNode.style));
			}
			this.blocks[left.blockIndex].textNodes = currentLineNodes;
			if (blocks.length) {
				const newBlocks = blocks.map(
					block =>
						new Block(
							block.textNodes.map(node => new TextNode(node.text, node.style)),
							block.style
						)
				);
				this.blocks.splice(left.blockIndex + 1, 0, ...newBlocks);
				const blockEnd = this.blocks[left.blockIndex + blocks.length].getBlockEnd();
				this.blocks[left.blockIndex + blocks.length].textNodes.push(...lastLineNodes);
				this.selection.set({
					range: false,
					left: {
						blockIndex: left.blockIndex + blocks.length,
						...blockEnd,
					},
				});
			} else {
				const blockEnd = this.blocks[left.blockIndex].getBlockEnd();
				this.blocks[left.blockIndex].textNodes.push(...lastLineNodes);
				this.selection.set({
					range: false,
					left: {
						blockIndex: left.blockIndex,
						...blockEnd,
					},
				});
			}
			return true;
		}
	}
	add(data: any): boolean {
		const { range } = this.selection.get();

		if (range) {
			this._splitRange();
			this._removeRange();
		}
		if (data.newBlock) {
			this.insertBlock();
			return true;
		}

		const { offset, blockIndex, textIndex } = this.selection.get().left;
		if (
			this._tempNode &&
			this._tempNode.offset === offset &&
			this._tempNode.blockIndex === blockIndex &&
			this._tempNode.textIndex === textIndex
		) {
			return this._createNodeFromTemp(data);
		}
		const textNodes = this.blocks[blockIndex].textNodes;
		const textNode = textNodes[textIndex];
		if (
			textNode.style.link &&
			textNode.text.length === offset &&
			(!textNodes[textIndex + 1] || !textNodes[textIndex + 1].style.link)
		) {
			const newTextNode = new TextNode(data, copyWithout(textNode.style, { link: true }));
			textNodes.splice(textIndex + 1, 0, newTextNode);
			this.selection.set({
				range: false,
				left: {
					offset: data.length,
					blockIndex,
					textIndex: textIndex + 1,
				},
			});
		} else if (textNode.style.link && offset === 0) {
			const newTextNode = new TextNode(data, copyWithout(textNode.style, { link: true }));
			textNodes.splice(textIndex, 0, newTextNode);
			this.selection.set({
				range: false,
				left: {
					offset: data.length,
					blockIndex,
					textIndex,
				},
			});
		} else {
			textNodes[textIndex].insert(data, offset);
			this.selection.set({
				range: false,
				left: {
					offset: offset + data.length,
					blockIndex,
					textIndex,
				},
			});
		}
		return true;
	}
	remove(prev: boolean): boolean {
		const { range } = this.selection.get();

		if (range) {
			this._splitRange();
			this._removeRange();
			return true;
		} else {
			return this._removeSymbol(prev);
		}
	}
	update({ modifier, modifierValue }): boolean {
		const { range, left } = this.selection.get();

		switch (modifier) {
			// block modifiers
			case Modifier.align:
			case Modifier.style:
			case Modifier.blockquote:
				this.eachSelectedBlock(block => {
					block.style = {
						...block.style,
						[modifier]: modifierValue,
					};
					if (modifier === Modifier.style && modifierValue !== "p") {
						block.textNodes.forEach(textNode => {
							textNode.style = copyWithout(textNode.style, {
								[Modifier.fontSize]: true,
							});
						});
					}
				});
				return true;
			// text modifers
			case Modifier.strike:
			case Modifier.underline:
			case Modifier.italic:
			case Modifier.bold:
			case Modifier.color:
			case Modifier.background:
			case Modifier.fontFamily:
			case Modifier.fontSize:
				if (!range) {
					this._createTempTextNode({ modifier, modifierValue });
					return false;
				}
				this._splitRange();
				this.eachSelectedNode(node => {
					node.style = {
						...node.style,
						[modifier]: modifierValue,
					};
				});
				return true;
			case Modifier.link: {
				const leftTextNode = this.blocks[left.blockIndex].textNodes[left.textIndex];
				if (modifier === undefined || leftTextNode.style.link !== undefined) {
					this._eachLink(node => {
						node.style = {
							...node.style,
							[modifier]: modifierValue,
						};
					});
				} else {
					if (range) {
						this._splitRange();
					}
					this.eachSelectedNode(node => {
						node.style = {
							...node.style,
							[modifier]: modifierValue,
						};
					});
				}
				return true;
			}
		}
	}
	clearStyles(): boolean {
		if (!this.selection.get().range) {
			return false;
		}
		this._splitRange();
		this.eachSelectedNode(node => {
			node.style = emptyStyle;
		});
		return true;
	}
	balance() {
		const { left, right, range } = this.selection.get();
		const leftOffset = this.blocks[left.blockIndex].nodePositionToOffset(left.textIndex, left.offset);
		let rightOffset;
		if (range) {
			rightOffset = this.blocks[right.blockIndex].nodePositionToOffset(right.textIndex, right.offset);
		}

		let notChanged = true;

		this.blocks.forEach(block => {
			if (block.balance()) {
				notChanged = false;
			}
		});

		if (notChanged) {
			return;
		}

		this.selection.set({
			range,
			left: {
				blockIndex: left.blockIndex,
				...this.blocks[left.blockIndex].offsetToNodePosition(leftOffset),
			},
			right: rightOffset && {
				blockIndex: right.blockIndex,
				...this.blocks[right.blockIndex].offsetToNodePosition(rightOffset),
			},
		});
		this._repaint();
	}
	getLinkInfo() {
		let text = "";
		let link;

		this._eachLink(textNode => {
			text += textNode.text;
			link = textNode.style.link;
		});
		return { text, link };
	}
	updateLinkText(text: string) {
		const { left, range } = this.selection.get();
		const textNodes = this.blocks[left.blockIndex].textNodes;
		const link = textNodes[left.textIndex].style.link;
		const textNode = new TextNode(text, {});
		if (link) {
			let linkLeftIndex = left.textIndex;
			let linkRightIndex = left.textIndex;
			while (textNodes[linkLeftIndex - 1] && textNodes[linkLeftIndex - 1].style.link === link) {
				linkLeftIndex--;
			}
			while (textNodes[linkRightIndex + 1] && textNodes[linkRightIndex + 1].style.link === link) {
				linkRightIndex++;
			}

			textNodes.splice(linkLeftIndex, linkRightIndex - linkLeftIndex + 1, textNode);
			this.selection.set({
				range: true,
				left: {
					blockIndex: left.blockIndex,
					textIndex: linkLeftIndex,
					offset: 0,
				},
				right: {
					blockIndex: left.blockIndex,
					textIndex: linkLeftIndex,
					offset: text.length,
				},
			});
		} else {
			if (range) {
				this._splitRange();
				this._removeRange();
				const { blockIndex, textIndex } = this.selection.get().left;
				this.blocks[blockIndex].textNodes.splice(textIndex + 1, 0, textNode);
				this.selection.set({
					range: true,
					left: {
						blockIndex,
						textIndex: textIndex + 1,
						offset: 0,
					},
					right: {
						blockIndex,
						textIndex: textIndex + 1,
						offset: text.length,
					},
				});
			} else {
				let index;
				if (this.blocks[left.blockIndex].isEmpty()) {
					textNodes.splice(0, 1);
					index = 0;
				} else {
					const splitResult = this.blocks[left.blockIndex].splitTextNode(
						left.textIndex,
						left.offset
					);
					index = splitResult === SplitResult.offsetStart ? left.textIndex : left.textIndex + 1;
				}
				textNodes.splice(index, 0, textNode);
				this.selection.set({
					range: true,
					left: {
						blockIndex: left.blockIndex,
						textIndex: index,
						offset: 0,
					},
					right: {
						blockIndex: left.blockIndex,
						textIndex: index,
						offset: text.length,
					},
				});
			}
		}
	}
	private _splitRange() {
		// left selection must have offset = 0
		// right selection must have offset = end of textnode
		const { range, left, right } = this.selection.get();

		if (!range) {
			return;
		}

		// 1 - left and right are single text node
		if (left.blockIndex === right.blockIndex && left.textIndex === right.textIndex) {
			let rightTextIndex = left.textIndex;
			let rightOffset = right.offset;
			const leftSplitResult = this.blocks[left.blockIndex].splitTextNode(left.textIndex, left.offset);
			// in this case left cant return SplitResult.offsetEnd coz this textNode has right
			// if node was splited need to update right text index and right offset
			if (leftSplitResult === SplitResult.complete) {
				rightTextIndex += 1;
				rightOffset -= left.offset;
			}
			this.blocks[right.blockIndex].splitTextNode(rightTextIndex, rightOffset);
			const selection = {
				range,
				left: {
					blockIndex: left.blockIndex,
					textIndex: rightTextIndex,
					offset: 0,
				},
				right: {
					blockIndex: right.blockIndex,
					textIndex: rightTextIndex,
					offset: rightOffset,
				},
			};
			this.selection.set(selection);
			return;
		}
		// 2 - left and right located in one block, so left split can move right position
		if (left.blockIndex === right.blockIndex) {
			let leftTextIndex = left.textIndex;
			let rightTextIndex = right.textIndex;

			const leftSplitResult = this.blocks[left.blockIndex].splitTextNode(leftTextIndex, left.offset);
			// move selection to next node
			if (leftSplitResult === SplitResult.offsetEnd) {
				leftTextIndex += 1;
			}
			// move selection to next node and update right position
			if (leftSplitResult === SplitResult.complete) {
				rightTextIndex += 1;
				leftTextIndex += 1;
			}

			this.blocks[right.blockIndex].splitTextNode(rightTextIndex, right.offset);
			const selection = {
				range,
				left: {
					blockIndex: left.blockIndex,
					textIndex: leftTextIndex,
					offset: 0,
				},
				right: {
					blockIndex: right.blockIndex,
					textIndex: rightTextIndex,
					offset: right.offset,
				},
			};
			this.selection.set(selection);
			return;
		}
		// 3 - left and right located in diffrent blocks
		if (left.blockIndex !== right.blockIndex) {
			let leftTextIndex = left.textIndex;
			let leftBlockIndex = left.blockIndex;

			const leftSplitResult = this.blocks[left.blockIndex].splitTextNode(left.textIndex, left.offset);
			this.blocks[right.blockIndex].splitTextNode(right.textIndex, right.offset);

			if (leftSplitResult === SplitResult.offsetEnd) {
				// if this textnode have next node
				if (this.blocks[leftBlockIndex].textNodes[leftTextIndex + 1]) {
					leftTextIndex += 1;
				} else {
					leftTextIndex = 0;
					leftBlockIndex += 1;
				}
			}
			if (leftSplitResult === SplitResult.complete) {
				leftTextIndex += 1;
			}
			const selection = {
				range,
				left: {
					blockIndex: leftBlockIndex,
					textIndex: leftTextIndex,
					offset: 0,
				},
				right: {
					blockIndex: right.blockIndex,
					textIndex: right.textIndex,
					offset: right.offset,
				},
			};
			this.selection.set(selection);
		}
	}
	private _splitCarret() {
		// need for split block into text nodes
		const { range, left } = this.selection.get();

		if (range) {
			return;
		}

		const splitResult = this.blocks[left.blockIndex].splitTextNode(left.textIndex, left.offset);

		if (splitResult === SplitResult.complete) {
			const selection = {
				range,
				left: {
					blockIndex: left.blockIndex,
					textIndex: left.textIndex + 1,
					offset: 0,
				},
			};
			this.selection.set(selection);
		}
	}
	private _removeRange() {
		// call only after splitRange
		const { left, right } = this.selection.get();
		if (!right) {
			return;
		}
		if (this.selection.isFull()) {
			this.clear();
			return;
		}
		if (left.blockIndex === right.blockIndex) {
			// selection contains full block
			if (
				left.textIndex === 0 &&
				right.textIndex === this.blocks[right.blockIndex].textNodes.length - 1
			) {
				this.blocks[left.blockIndex].clear();
				this.selection.set({
					range: false,
					left: {
						...left,
						offset: 0,
					},
				});
			} else {
				this.blocks[left.blockIndex].textNodes.splice(
					left.textIndex,
					right.textIndex - left.textIndex + 1
				);
				if (left.textIndex === 0) {
					this.selection.set({
						range: false,
						left: {
							blockIndex: left.blockIndex,
							textIndex: 0,
							offset: 0,
						},
					});
				} else {
					this.selection.set({
						range: false,
						left: {
							blockIndex: left.blockIndex,
							textIndex: left.textIndex - 1,
							offset: this.blocks[left.blockIndex].textNodes[left.textIndex - 1].text.length,
						},
					});
				}
			}
		} else {
			const isFullLeftRemove = this.blocks[left.blockIndex].removeNodes(left.textIndex, true);
			const isFullRightRemove = this.blocks[right.blockIndex].removeNodes(right.textIndex, false);

			const leftBlockEnd = this.blocks[left.blockIndex].getBlockEnd();

			switch (true) {
				case isFullLeftRemove && isFullRightRemove:
					this.blocks[left.blockIndex].clear();
					break;
				case isFullLeftRemove && !isFullRightRemove:
					this.blocks[left.blockIndex].textNodes = this.blocks[right.blockIndex].textNodes;
					break;
				case !isFullLeftRemove && !isFullRightRemove:
					this.blocks[left.blockIndex].textNodes.push(...this.blocks[right.blockIndex].textNodes);
					break;
				case !isFullLeftRemove && isFullRightRemove:
					break;
			}

			for (let i = right.blockIndex; i > left.blockIndex; i--) {
				this.removeBlock(i); // FIX
			}

			if (isFullLeftRemove) {
				this.selection.set({
					range: false,
					left: {
						blockIndex: left.blockIndex,
						textIndex: left.textIndex,
						offset: 0,
					},
				});
			} else {
				this.selection.set({
					range: false,
					left: {
						blockIndex: left.blockIndex,
						...leftBlockEnd,
					},
				});
			}
		}
	}
	private _removeSymbol(prev: boolean): boolean {
		const { offset, blockIndex, textIndex } = this.selection.get().left;

		const selection = {
			range: false,
			left: { offset, blockIndex, textIndex },
		};

		const blockEnd = this.blocks[blockIndex].getBlockEnd();
		const textNodes = this.blocks[blockIndex].textNodes;
		const textNode = textNodes[textIndex];

		let repeat = false;

		switch (true) {
			case this.blocks[blockIndex].isEmpty():
				if (blockIndex === 0 && prev) {
					if (!isEmptyBlockStyle(this.blocks[0].style)) {
						this.blocks[0].style = emptyStyle;
						return true;
					}
					return false;
				}
				if (!prev && !this.blocks[blockIndex + 1]) {
					return false;
				}
				this.removeBlock(blockIndex);
				if (blockIndex === 0) {
					selection.left.offset = 0;
					selection.left.textIndex = 0;
				} else {
					if (prev) {
						selection.left = {
							blockIndex: blockIndex - 1,
							...this.blocks[blockIndex - 1].getBlockEnd(),
						};
					}
				}
				break;
			case offset === 0 && textIndex === 0 && prev: // move all nodes of this block to previous
				if (blockIndex === 0) {
					return false;
				}
				if (this.blocks[blockIndex - 1].isEmpty()) {
					this.blocks[blockIndex - 1].textNodes = textNodes;
					this.removeBlock(blockIndex);
					selection.left = {
						blockIndex: blockIndex - 1,
						textIndex: 0,
						offset: 0,
					};
				} else {
					const previousBlockEnd = this.blocks[blockIndex - 1].getBlockEnd();
					this.blocks[blockIndex - 1].textNodes = this.blocks[blockIndex - 1].textNodes.concat(
						textNodes
					);
					this.removeBlock(blockIndex);
					selection.left = {
						blockIndex: blockIndex - 1,
						...previousBlockEnd,
					};
				}
				break;
			case blockEnd.offset === offset && blockEnd.textIndex === textIndex && !prev: {
				// move all nodes of next block to this block
				const nextBlock = this.blocks[blockIndex + 1];

				if (!nextBlock) {
					return;
				}

				if (nextBlock.isEmpty()) {
					this.removeBlock(blockIndex + 1);
					return true;
				}
				this.blocks[blockIndex].textNodes = this.blocks[blockIndex].textNodes.concat(
					nextBlock.textNodes
				);
				this.removeBlock(blockIndex + 1);
				break;
			}
			default: {
				const removeResult = textNode.remove(offset, prev);
				switch (removeResult) {
					case RemoveTextResult.nullTextLength:
						if (textNodes.length > 1) {
							textNodes.splice(textIndex, 1);
							if (textIndex === 0) {
								selection.left = {
									blockIndex,
									textIndex: 0,
									offset: 0,
								};
							} else {
								selection.left = {
									blockIndex,
									textIndex: textIndex - 1,
									offset: textNodes[textIndex - 1].text.length,
								};
							}
						} else {
							textNode.style = emptyStyle;
							selection.left = {
								textIndex,
								blockIndex,
								offset: 0,
							};
						}
						break;
					case RemoveTextResult.complete:
						if (prev) {
							selection.left = {
								blockIndex,
								textIndex,
								offset: offset - 1,
							};
						}
						break;
					case RemoveTextResult.forceRemoveNext:
						selection.left = {
							blockIndex,
							textIndex: textIndex + 1,
							offset: 0,
						};
						repeat = true;
						break;
					case RemoveTextResult.forceRemovePrevious:
						selection.left = {
							blockIndex,
							textIndex: textIndex - 1,
							offset: textNodes[textIndex - 1].text.length,
						};
						repeat = true;
						break;
				}
			}
		}

		this.selection.set(selection);
		if (repeat) {
			return this._removeSymbol(prev);
		}

		return true;
	}
	private _eachLink(fn: (textNode: TextNode) => any) {
		const left = this.selection.get().left;
		const link = this.blocks[left.blockIndex].textNodes[left.textIndex].style.link;
		if (!link) {
			return;
		}

		const block = this.blocks[left.blockIndex];
		const textNodes = block.textNodes;

		const linkNodes = [];

		for (let i = left.textIndex; i >= 0; i--) {
			if (textNodes[i].style.link !== link) {
				break;
			}
			linkNodes.unshift(textNodes[i]);
		}

		for (let i = left.textIndex + 1; i < textNodes.length; i++) {
			if (textNodes[i].style.link !== link) {
				break;
			}
			linkNodes.push(textNodes[i]);
		}
		linkNodes.forEach(fn);
	}
	private _createTempTextNode({ modifier, modifierValue }) {
		const { offset, blockIndex, textIndex } = this.selection.get().left;

		let nodeStyle;
		if (
			this._tempNode &&
			this._tempNode.offset === offset &&
			this._tempNode.blockIndex === blockIndex &&
			this._tempNode.textIndex === textIndex
		) {
			nodeStyle = this._tempNode.style;
		} else {
			nodeStyle = this.blocks[blockIndex].textNodes[textIndex].style;
		}

		const tempStyle = {
			...nodeStyle,
			[modifier]: modifierValue,
		};

		const textNodes = this.blocks[blockIndex].textNodes;
		if (tempStyle.link && (!textNodes[textIndex + 1] || !textNodes[textIndex + 1].style.link)) {
			delete tempStyle.link;
		}

		this._tempNode = {
			style: tempStyle,
			blockIndex,
			textIndex,
			offset,
		};
	}
	private _createNodeFromTemp(data: string) {
		const { style, blockIndex, textIndex, offset } = this._tempNode;

		const splitResult = this.blocks[blockIndex].splitTextNode(textIndex, offset);
		const newTextIndex = splitResult === SplitResult.offsetStart ? textIndex : textIndex + 1;
		const textNode = new TextNode(data, style);

		if (this.blocks[blockIndex].isEmpty()) {
			this.blocks[blockIndex].textNodes = [textNode];
		} else {
			this.blocks[blockIndex].textNodes.splice(newTextIndex, 0, textNode);
		}

		this.selection.set({
			range: false,
			left: {
				blockIndex,
				textIndex: newTextIndex,
				offset: data.length,
			},
		});
		this._tempNode = null;
		return true;
	}
}
